// 条件全体表示グリッド（R 室内音 / R MTG先端振動 / L 個別グラフ表示）で
// 縦軸をページ内の全グラフで統一するための算出ロジック。
//
// 呼び出し側の契約: 実際に <Line> で描画している系列だけを渡すこと。
// vel / rpm（補助 X 軸の目盛り整形用）や NG_freq（X 参照線）を混ぜると domain が壊れる。

export type YBounds = [min: number, max: number];

// 目盛り刻みの決め方。span をこの数で割った値を 1/2/5×10^n へ丸める。
const TICK_DIVISIONS = 10;
// スライダーの刻み。全域をこの数で分割する。
const SLIDER_DIVISIONS = 200;
// min === max のときに広げる比率（0 幅 domain 回避）。
const DEGENERATE_RATIO = 0.05;

// 浮動小数の桁あふれ（0.1 * 3 = 0.30000000000000004 等）を落とす。
const trim = (v: number): number => Number(v.toPrecision(12));

/**
 * 描画中の系列群から有限値の min / max を取る。
 * null / NaN / ±Infinity は除外し、有効値が 1 件も無ければ undefined を返す
 * （呼び出し側は domain 未指定＝recharts 既定にフォールバックする）。
 */
export function computeSharedYBounds(
    seriesList: ReadonlyArray<ReadonlyArray<number | null | undefined> | undefined>
): YBounds | undefined {
    let min = Infinity;
    let max = -Infinity;
    for (const series of seriesList) {
        if (!series) continue;
        for (const value of series) {
            if (typeof value !== "number" || !Number.isFinite(value)) continue;
            if (value < min) min = value;
            if (value > max) max = value;
        }
    }
    if (min === Infinity || max === -Infinity) return undefined;
    return [min, max];
}

/**
 * 軸端が読みやすい値になるよう、min / max を外側の nice 値へ丸める。
 * domain を明示すると recharts は端点をそのまま軸端に使う（getTickValuesFixedDomain）ため、
 * 端点自体を丸めておく。丸めは必ず外側なので描画データがクリップされることはない。
 */
export function niceDomain([min, max]: YBounds): YBounds {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [min, max];
    let lo = min;
    let hi = max;
    if (lo === hi) {
        const delta = Math.abs(lo) * DEGENERATE_RATIO || 1;
        lo -= delta;
        hi += delta;
    }
    const step = niceStep(hi - lo);
    return [trim(Math.floor(lo / step) * step), trim(Math.ceil(hi / step) * step)];
}

/**
 * 軸の全域（端から端）を4等分した5点を返す。隣接する値の差は必ず span/4 になるため、
 * 統一 ON/OFF や domain の丸め有無にかかわらず、目盛りは常に等間隔になる。
 */
export function quarterTicks([min, max]: YBounds): number[] {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [min, max];
    if (min === max) return [min];
    const span = max - min;
    return [0, 1, 2, 3, 4].map((i) => trim(min + (span * i) / 4));
}

/** 縦軸スライダーの刻み。全域を一定数で分割する。 */
export function sliderStep([lo, hi]: YBounds): number {
    const span = hi - lo;
    if (!Number.isFinite(span) || span <= 0) return 1;
    return trim(span / SLIDER_DIVISIONS);
}

/** スライダー操作値を step 格子へ丸め、浮動小数の桁あふれを落とす。 */
export function snapToStep(value: number, step: number): number {
    if (!Number.isFinite(step) || step <= 0) return trim(value);
    return trim(Math.round(value / step) * step);
}

/**
 * 選択幅を保ったまま範囲を平行移動する（スライダー中央のドラッグ / 上下ボタン用）。
 * 全域の外へは出さず、端に達したら幅を維持したまま止まる。
 */
export function panRange(range: YBounds, bounds: YBounds, delta: number, step: number): YBounds {
    const width = range[1] - range[0];
    const [min, max] = bounds;
    // 幅を保つため lo の可動域は [min, max - width]。
    const maxLo = max - width;
    if (!(maxLo > min)) return [min, max];
    const lo = Math.min(Math.max(snapToStep(range[0] + delta, step), min), maxLo);
    return [trim(lo), trim(lo + width)];
}

// span を TICK_DIVISIONS で割った値を 1 / 2 / 5 × 10^n へ切り上げる。
function niceStep(span: number): number {
    if (!Number.isFinite(span) || span <= 0) return 1;
    const rough = span / TICK_DIVISIONS;
    const base = Math.pow(10, Math.floor(Math.log10(rough)));
    const frac = rough / base;
    const mult = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
    return trim(mult * base);
}
